import React, { useState, useRef } from 'react';
import { Plus, Trash2, CheckCircle, Send, Upload, X, FileText, Image, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PublicPaymentRequestCsvTriggers from '@/components/finance/salaries-import-csv/PublicPaymentRequestCsvTriggers';
import { Separator } from '@/components/ui/separator';
import { CurrencySelect } from '@/components/ui/currency-select';
import { usePublicDepartments, usePublicCostPositions } from '@/hooks/finance/usePublicPaymentData';
import { usePublicApprovers } from '@/hooks/finance/usePublicApprovers';
import { ApprovalWorkflow } from '@/components/finance/ApprovalWorkflow';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { toast } from 'sonner';
import { z } from 'zod';
import { BUAllocationEditor, AllocationValue, allocationsAreValid } from '@/components/finance/BUAllocationEditor';
import { DEFAULT_BUSINESS_UNIT, BusinessUnit } from '@/constants/businessUnits';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

// Validation schema - title removed since we use cost position name
const requestSchema = z.object({
  requester_first_name: z.string().trim().min(1, "First name is required").max(50, "First name too long"),
  requester_last_name: z.string().trim().min(1, "Last name is required").max(50, "Last name too long"),
  requester_email: z.string().trim().email("Invalid email").max(255, "Email too long"),
  description: z.string().trim().min(1, "Purpose/Description is required").max(1000, "Purpose too long"),
  vendor_name: z.string().trim().min(1, "Vendor name is required").max(200, "Vendor name too long"),
  vendor_bank_name: z.string().trim().min(1, "Bank name is required").max(100, "Bank name too long"),
  vendor_bank_account: z.string().trim().min(1, "Account number is required").max(50, "Account number too long"),
  vendor_bank_branch: z.string().trim().max(100, "Branch too long").optional().or(z.literal('')),
});

const PublicPaymentRequestPage = () => {
  const { data: departments } = usePublicDepartments();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const { data: filteredCategories } = usePublicCostPositions(selectedDepartmentId);
  const { data: approvers, isLoading: isLoadingApprovers } = usePublicApprovers(selectedDepartmentId);

  // Get the primary approver (first by approval level)
  const primaryApprover = approvers?.[0] || null;

  const [formData, setFormData] = useState({
    requester_first_name: '',
    requester_last_name: '',
    requester_email: '',
    department_id: '',
    category_id: '',
    description: '',
    vendor_name: '',
    vendor_bank_name: '',
    vendor_bank_account: '',
    vendor_bank_branch: '',
    payment_method: 'bank_transfer',
    preferred_payment_date: '',
    currency: DEFAULT_CURRENCY,
    business_unit: DEFAULT_BUSINESS_UNIT as BusinessUnit,
  });

  const [buAllocations, setBuAllocations] = useState<AllocationValue[]>([
    { business_unit: DEFAULT_BUSINESS_UNIT, percentage: 100 },
  ]);

  const [items, setItems] = useState<LineItem[]>([
    { id: '1', description: '', quantity: 1, unit_price: 0, amount: 0 },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [requestNumber, setRequestNumber] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    addFiles(newFiles);
    e.target.value = '';
  };

  const addFiles = (newFiles: File[]) => {
    const remaining = MAX_FILES - selectedFiles.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }
    const validFiles: File[] = [];
    for (const file of newFiles.slice(0, remaining)) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 5MB limit`);
        continue;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name} is not a supported file type`);
        continue;
      }
      validFiles.push(file);
    }
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // Remove data:... prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileText className="h-4 w-4 text-red-500" />;
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // When department changes, update the selected department for filtering and reset category
    if (field === 'department_id') {
      setSelectedDepartmentId(value as string || null);
      setFormData(prev => ({ ...prev, department_id: value as string, category_id: '' }));
    }
  };

  const handleItemChange = (id: string, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.amount = Number(updated.quantity) * Number(updated.unit_price);
        }
        return updated;
      }
      return item;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit_price: 0,
      amount: 0
    }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    return { subtotal, total: subtotal };
  };

  const { subtotal, total } = calculateTotals();

  // Get title from selected category name
  const getTitle = () => {
    if (formData.category_id && filteredCategories) {
      const category = filteredCategories.find(c => c.id === formData.category_id);
      return category?.name || 'Payment Request';
    }
    return 'Payment Request';
  };

  const handleSubmit = async () => {
    // Validate form
    const validationResult = requestSchema.safeParse({
      requester_first_name: formData.requester_first_name,
      requester_last_name: formData.requester_last_name,
      requester_email: formData.requester_email,
      description: formData.description,
      vendor_name: formData.vendor_name,
      vendor_bank_name: formData.vendor_bank_name,
      vendor_bank_account: formData.vendor_bank_account,
      vendor_bank_branch: formData.vendor_bank_branch,
    });

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    if (!formData.department_id) {
      toast.error('Please select a department');
      return;
    }

    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    if (!allocationsAreValid(buAllocations) || buAllocations.length === 0) {
      toast.error('Business unit allocations must total 100%');
      return;
    }

    if (items.every(i => !i.description || i.amount <= 0)) {
      toast.error('Please add at least one line item');
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert files to base64
      const documents = await Promise.all(
        selectedFiles.map(async (file) => ({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          base64: await fileToBase64(file),
        }))
      );

      const { data, error } = await supabase.functions.invoke('public-payment-request', {
        body: {
          ...formData,
          title: getTitle(),
          department_id: formData.department_id || undefined,
          category_id: formData.category_id || undefined,
          amount: subtotal,
          vat_applicable: false,
          vat_amount: 0,
          total_amount: total,
          items: items.filter(i => i.description && i.amount > 0).map(({ id, ...item }) => item),
          documents: documents.length > 0 ? documents : undefined,
          business_unit: [...buAllocations].sort((a, b) => b.percentage - a.percentage)[0]?.business_unit || formData.business_unit,
          allocations: buAllocations,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      setRequestNumber(data.request_number);
      setIsSubmitted(true);
      toast.success('Payment request submitted successfully!');
    } catch (error: any) {
      console.error('Failed to submit:', error);
      toast.error(error.message || 'Failed to submit payment request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-6 space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Request Submitted!</h2>
            <p className="text-muted-foreground">
              Your payment request has been submitted successfully.
            </p>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Request Number</p>
              <p className="text-xl font-mono font-bold">{requestNumber}</p>
            </div>

            {/* Approval Workflow */}
            <ApprovalWorkflow
              approverName={primaryApprover?.full_name || null}
              isLoading={isLoadingApprovers}
            />

            <p className="text-sm text-muted-foreground">
              You will receive updates at <strong>{formData.requester_email}</strong>
            </p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Payment Request Form</h1>
          <p className="text-muted-foreground">Submit a payment request for approval</p>
        </div>

        <div className="grid gap-6">
          {/* Requester Information */}
          <Card>
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
              <CardDescription>Please provide your contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="requester_first_name">First Name *</Label>
                  <Input
                    id="requester_first_name"
                    placeholder="First name"
                    value={formData.requester_first_name}
                    onChange={(e) => handleInputChange('requester_first_name', e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requester_last_name">Last Name *</Label>
                  <Input
                    id="requester_last_name"
                    placeholder="Last name"
                    value={formData.requester_last_name}
                    onChange={(e) => handleInputChange('requester_last_name', e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requester_email">Your Email *</Label>
                  <Input
                    id="requester_email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.requester_email}
                    onChange={(e) => handleInputChange('requester_email', e.target.value)}
                    maxLength={255}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Request Details */}
          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
              <CardDescription>Basic information about the payment request</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(v) => handleInputChange('department_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments?.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(v) => handleInputChange('category_id', v)}
                    disabled={!formData.department_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.department_id ? "Select category" : "Select department first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories?.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Unit Allocation *</Label>
                <BUAllocationEditor
                  value={buAllocations}
                  onChange={setBuAllocations}
                />
                <p className="text-xs text-muted-foreground">
                  Split this payment across business units. Percentages must total 100%.
                </p>
              </div>
              <PublicPaymentRequestCsvTriggers
                departments={departments}
                categories={filteredCategories}
                selectedDepartmentId={formData.department_id}
                selectedCategoryId={formData.category_id}
              />

              <div className="space-y-2">
                <Label htmlFor="description">Purpose / Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Explain why this payment is needed"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  maxLength={1000}
                />
              </div>
            </CardContent>
          </Card>

          {/* Vendor Information */}
          <Card>
            <CardHeader>
              <CardTitle>Receiver Account Information</CardTitle>
              <CardDescription>Details about who will receive the payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vendor_name">Vendor Name *</Label>
                <Input
                  id="vendor_name"
                  placeholder="Name of vendor or payee"
                  value={formData.vendor_name}
                  onChange={(e) => handleInputChange('vendor_name', e.target.value)}
                  maxLength={200}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_name">Bank Name *</Label>
                  <Input
                    id="vendor_bank_name"
                    placeholder="e.g., CBE"
                    value={formData.vendor_bank_name}
                    onChange={(e) => handleInputChange('vendor_bank_name', e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_account">Account Number *</Label>
                  <Input
                    id="vendor_bank_account"
                    placeholder="Account number"
                    value={formData.vendor_bank_account}
                    onChange={(e) => handleInputChange('vendor_bank_account', e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_branch">Branch *</Label>
                  <Input
                    id="vendor_bank_branch"
                    placeholder="Branch name"
                    value={formData.vendor_bank_branch}
                    onChange={(e) => handleInputChange('vendor_bank_branch', e.target.value)}
                    maxLength={100}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Line Items</CardTitle>
                  <CardDescription>Itemized breakdown of the payment</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="currency" className="text-sm text-muted-foreground">Currency</Label>
                  <CurrencySelect
                    value={formData.currency}
                    onValueChange={(v) => handleInputChange('currency', v)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity || ''}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        placeholder="Unit Price"
                        value={item.unit_price || ''}
                        onChange={(e) => handleItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32 flex items-center justify-end font-medium">
                      {formatCurrency(item.amount, formData.currency)}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(total, formData.currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>How and when should this be paid</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(v) => handleInputChange('payment_method', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_payment_date">Preferred Payment Date</Label>
                  <Input
                    id="preferred_payment_date"
                    type="date"
                    value={formData.preferred_payment_date}
                    onChange={(e) => handleInputChange('preferred_payment_date', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Supporting Documents */}
          <Card>
            <CardHeader>
              <CardTitle>Supporting Documents</CardTitle>
              <CardDescription>Upload invoices, receipts, or other proof documents (max {MAX_FILES} files, 5MB each)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-primary/50`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files));
                }}
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Drag & drop files here or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, JPG, PNG, XLSX accepted
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls"
                  onChange={handleFileSelect}
                />
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file)}
                        <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeFile(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="min-w-[200px]"
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentRequestPage;
